import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../ui/kit';
import type { PracticeSummary } from '../../auth/session';
import { initial, businessColor } from '../../layout/companies';
import { clientFacts, stateOf, usePracticeClients, useOpenClientBooks, type PracticeClient } from './data';

/**
 * "Find a client" (⌘K in the practice, and Switch client in a client's books). Client companies are
 * not in the company switcher (there can be hundreds), so this is how a practice reaches them.
 * Enter opens the client page; with `books`, it opens the client's books instead.
 */
export function ClientFinder({ practice, books = false, onClose }: { practice: PracticeSummary; books?: boolean; onClose: () => void }) {
  const clients = usePracticeClients(practice.id);
  const navigate = useNavigate();
  const openBooks = useOpenClientBooks();
  const [text, setText] = useState('');
  const [hl, setHl] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    inputRef.current?.focus();
    return () => { if (returnTo && document.contains(returnTo)) returnTo.focus(); };
  }, []);

  const matches = useMemo(() => {
    const q = text.trim().toLowerCase();
    const all = (clients.data ?? []).filter((c) => !books || c.state === 'active' || c.state === 'practice_managed');
    const found = q
      ? all.filter((c) => [c.name, c.registration_number, c.vat_number, c.income_tax_number].some((v) => v?.toLowerCase().includes(q)))
      : all;
    return found.slice(0, 30);
  }, [clients.data, text, books]);
  useEffect(() => { setHl(0); }, [text]);

  const choose = (c: PracticeClient | undefined) => {
    if (!c) return;
    onClose();
    navigate(books ? openBooks(practice, c) : `/practice/clients/${c.id as string}`);
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHl((n) => Math.min(n + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHl((n) => Math.max(n - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(matches[hl]); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
    else if (e.key === 'Tab') e.preventDefault();
  };
  const optionId = (n: number) => `${listId}-opt-${n}`;

  return createPortal(
    <div className="scrim top" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="palette" role="dialog" aria-modal="true" aria-label={books ? 'Switch client' : 'Find a client'}>
        <div className="palette-input">
          <Icon name="search" size={18} />
          <input ref={inputRef} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown}
            placeholder="Find a client by name, registration or tax number" autoComplete="off" spellCheck={false} maxLength={100}
            aria-label="Find a client" role="combobox" aria-expanded={matches.length > 0} aria-controls={listId} aria-autocomplete="list"
            aria-activedescendant={matches.length ? optionId(hl) : undefined} />
          <span className="pop-kbd">Esc</span>
        </div>
        <div className="palette-results" id={listId} role="listbox" aria-label="Clients">
          <div className="pop-title" aria-hidden="true">{books ? `Open the books of a ${practice.name} client` : `${practice.name} clients`}</div>
          {matches.map((c, i) => (
            <div key={c.id} id={optionId(i)} className={`pop-item ${i === hl ? 'hl' : ''}`} role="option" aria-selected={i === hl}
              onMouseMove={() => { if (i !== hl) setHl(i); }} onMouseDown={(ev) => ev.preventDefault()} onClick={() => choose(c)}>
              <span className="tenant-avatar" style={{ background: businessColor(c.tenant_id as string) }} aria-hidden="true">{initial(c.name as string)}</span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span className="pi-title" style={{ display: 'block' }}>{c.name}</span>
                <span className="pi-sub" style={{ display: 'block' }}>{c.state === 'active' ? clientFacts(c) : stateOf(c).label}</span>
              </span>
            </div>
          ))}
          {clients.isLoading && <div className="pop-empty">Loading clients…</div>}
          {!clients.isLoading && matches.length === 0 && <div className="pop-empty">{text ? <>No client matches “{text}”.</> : 'No clients yet.'}</div>}
        </div>
      </div>
    </div>,
    document.body,
  );
}
